import {
  createElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { Crosshair1Icon, MinusIcon, PlusIcon } from '@radix-ui/react-icons';
import { Flex, Popover, Text, Tooltip } from '@raystack/apsara';
import clsx from 'clsx';

import { FloatingIconButton } from '@src/components/Custom';
import MapLayers from '@src/containers/Explore/subComponents/MapLayers/MapLayers';
import { useMapViewSelector } from '@src/contexts';
import { For, Show } from '@src/hoc';
import { useQueryParams } from '@src/hooks';
import { getCurrentPosition } from '@src/utils/MapCompareUtils';
import { locationErrorHandler } from '@src/utils/helpers/LocationHelper';

import { ReactComponent as Compass } from './icons/Compass.svg';
import { ReactComponent as MapOutline } from './icons/MapOutline.svg';

import styles from './MapViewActions.module.css';

interface PrimaryMapViewActions {
  id: string;
  name: string;
  icon: React.ReactNode;
  component: ({ onClose }: { onClose?: VoidFunction }) => React.ReactNode;
  disabled?: boolean;
  defaultOpen?: boolean;
  type?: 'popover' | 'modal';
}

interface PrimaryMapViewActionsProps {
  actions: PrimaryMapViewActions[];
  classes?: Partial<{ root: string; modal: string }>;
  toolKey?: string;
  onBeforeChange?: () => void;
}

interface PrimaryMapViewActionsRef {
  setActiveAction: (id: string | null, force?: boolean) => void;
  getActiveAction: () => string | null;
}

type PrimaryMapViewActionsQuery = { [key: string]: string };

export const PrimaryMapViewActions = forwardRef<
  PrimaryMapViewActionsRef,
  PrimaryMapViewActionsProps
>(({ actions, classes, toolKey = 'tool', onBeforeChange }, ref) => {
  const [queryParams, setQueryParams, flushQueryParams] =
    useQueryParams<PrimaryMapViewActionsQuery>([toolKey]);

  const active = queryParams[toolKey];
  const [isCompVisible, setIsCompVisible] = useState(true);

  const handleClick = useCallback(
    (id: string | null, force = false) => {
      if (!force && id === active) {
        setIsCompVisible(prev => !prev);
        return;
      }

      setIsCompVisible(() => true);
      onBeforeChange?.();
      setQueryParams({ [toolKey]: id });
      flushQueryParams();
    },
    [setQueryParams, toolKey, active, onBeforeChange, flushQueryParams]
  );

  const toggleClick = useCallback(
    (id: string) => handleClick(active === id ? null : id, true),
    [active, handleClick]
  );

  useImperativeHandle(
    ref,
    () => ({ setActiveAction: handleClick, getActiveAction: () => active }),
    [active, handleClick]
  );

  useEffect(() => {
    if (active !== null) return;

    const defaultOpenAction = actions.find(d => d.defaultOpen === true);
    if (!defaultOpenAction) return;

    handleClick(defaultOpenAction.id);
  }, []);

  // Find the active action for content rendering
  const activeAction = actions.find(action => action.id === active);

  return (
    <>
      <Flex
        direction='column'
        gap={2}
        align='center'
        className={clsx(styles.primaryActions, classes?.root)}
        key={active}
      >
        <For each={actions}>
          {({ id, name, icon, component, disabled = false, type }) => {
            const isActive = active === id;

            if (icon === null) return <></>;

            const iconButton = (
              <FloatingIconButton
                data-test-id={`toolbar-btb-${id}`}
                onClick={() =>
                  (type === 'popover' ? toggleClick : handleClick)(id)
                }
                disabled={disabled}
                active={isActive}
              >
                <Tooltip key={id} message={name} side='right'>
                  {icon}
                </Tooltip>
              </FloatingIconButton>
            );

            // For popover type, keep it in the loop as it needs to wrap the trigger
            return (
              <Show when={type === 'popover'} fallback={iconButton}>
                <Popover open={isActive}>
                  <Popover.Trigger asChild>{iconButton}</Popover.Trigger>
                  <Popover.Content
                    side='right'
                    sideOffset={8}
                    align='start'
                    className={styles.popoverContent}
                  >
                    {component({ onClose: () => toggleClick(id) })}
                  </Popover.Content>
                </Popover>
              </Show>
            );
          }}
        </For>
      </Flex>

      <Show
        when={
          !!activeAction &&
          !activeAction.disabled &&
          (!activeAction.type || activeAction.type === 'modal')
        }
      >
        <div
          className={clsx(styles.primaryActionComp, classes?.modal, {
            [styles.primaryActionCompVisible]: isCompVisible
          })}
          data-active={true}
        >
          {activeAction &&
            createElement(activeAction.component, {
              onClose: () => handleClick(activeAction.id)
            })}
        </div>
      </Show>
    </>
  );
});

PrimaryMapViewActions.displayName = 'PrimaryMapViewActions';

const MouseCoordinates = () => {
  const mapInstace = useMapViewSelector(store => store.mapInstance);
  const [coords, setCoords] = useState('');

  useEffect(() => {
    if (mapInstace) {
      const subs = mapInstace.onMouseMove(e => {
        const { lng, lat } = e.lngLat;
        setCoords([lat, lng].map(d => d.toFixed(5)).join(', '));
      });

      return () => {
        subs();
      };
    }
  }, [mapInstace]);

  return (
    <Show when={coords.length > 0}>
      <Text size={1} variant='secondary' className={styles.mouseCoordinates}>
        {coords}
      </Text>
    </Show>
  );
};

const CompassBearing = () => {
  const mapInstace = useMapViewSelector(store => store.mapInstance);

  const [bearing, setBearing] = useState(0);

  useEffect(() => {
    if (mapInstace) {
      setBearing(mapInstace.getBearing);

      const subs = mapInstace.onBearingChange(() =>
        setBearing(mapInstace.getBearing)
      );

      return () => {
        subs();
      };
    }
  }, [mapInstace]);

  return (
    <div className={styles.compassAndMouseMove}>
      <Compass
        data-test-id='compass'
        className={styles.compass}
        style={{ transform: `rotate(${bearing * -1}deg)` }}
        onClick={() => mapInstace?.setBearing(0)}
      />

      <MouseCoordinates />
    </div>
  );
};

const MapActions = () => {
  const mapInstace = useMapViewSelector(store => store.mapInstance);

  const intervalRef = useRef<null | number>(null);

  const zoomHandler = useCallback(
    (type: 'zoom-in' | 'zoom-out') => {
      if (intervalRef.current || !mapInstace) return;

      let count = 0;
      const multiplier = type === 'zoom-in' ? 1 : -1;

      intervalRef.current = setInterval(() => {
        count += 0.01;
        const newZoom = mapInstace.getZoom() + multiplier * count;
        mapInstace.zoomTo(newZoom, { animate: false });
      }, 30);
    },
    [mapInstace]
  );

  const stopRef = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    return stopRef;
  }, []);

  const buttons = useMemo(
    () => [
      {
        type: 'component',
        id: 'mapLayers',
        name: 'Map Layers',
        icon: <MapOutline />,
        component: <MapLayers />
      },
      {
        type: 'button',
        id: 'currentLocation',
        name: 'Current Location',
        icon: <Crosshair1Icon />,
        onClick: async () => {
          try {
            if (mapInstace) {
              const [lat, lng] = await getCurrentPosition();
              mapInstace.goTo([lng, lat]);
            }
          } catch (err) {
            locationErrorHandler(err);
          }
        }
      },
      {
        type: 'button',
        id: 'zoom-in',
        name: 'Zoom In',
        icon: <PlusIcon />,
        onMouseDown: () => zoomHandler('zoom-in'),
        onMouseLeave: stopRef,
        onMouseUp: stopRef,
        onTouchStart: () => zoomHandler('zoom-in'),
        onTouchEnd: stopRef
      },
      {
        type: 'button',
        id: 'zoom-out',
        name: 'Zoom Out',
        icon: <MinusIcon />,
        onMouseDown: () => zoomHandler('zoom-out'),
        onMouseLeave: stopRef,
        onMouseUp: stopRef,
        onTouchStart: () => zoomHandler('zoom-out'),
        onTouchEnd: stopRef
      },
      {
        type: 'icon',
        id: 'compass',
        name: 'Compass',
        icon: <CompassBearing />
      }
    ],
    [mapInstace, zoomHandler]
  );

  return (
    <For each={buttons}>
      {({ id, name, icon, type, ...props }) => {
        const btnJSX = (
          <FloatingIconButton data-test-id={`toolbar-btb-${id}`} {...props}>
            <Tooltip key={id} message={name} side='left'>
              {icon}
            </Tooltip>
          </FloatingIconButton>
        );

        if (type === 'button') return btnJSX;

        if (type === 'component') {
          return (
            <Popover>
              <Popover.Trigger asChild>{btnJSX}</Popover.Trigger>

              <Popover.Content
                side='left'
                align='start'
                sideOffset={8}
                className={styles.popoverContent}
              >
                {props.component}
              </Popover.Content>
            </Popover>
          );
        }

        return icon;
      }}
    </For>
  );
};

// Primary Map actions should be passed as children, as they will keep changing where ever we use the map
// in the application
export const MapViewActions = () => {
  return (
    <Flex
      direction='column'
      gap={2}
      align='center'
      className={styles.mapViewActions}
    >
      <MapActions />
    </Flex>
  );
};
